import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import { useAuth, useI18N, usePostFlagNote } from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import {
  AddPhotoImageData,
  BusinessSociabilitySaysApproved,
} from "common/types";
import Zoom from "react-medium-image-zoom";
import AnalyticsService from "@/services/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";

type FlagVenueNoteProps = {
  note?: BusinessSociabilitySaysApproved;
  onClose: () => void;
  venueDetails: any;
};

function FlagVenueNote({ note, onClose, venueDetails }: FlagVenueNoteProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();

  const { user } = useAuth();

  const showToast = useCustomToast();

  const [noteText, setNoteText] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const { data, error, mutate, status } = usePostFlagNote();

  useEffect(() => {
    if (error) {
      console.log("useFlagPhoto", error);

      AnalyticsService.trackGAEventHandler("flagAccessibilityReportError");
      AnalyticsService.trackMx("flagAccessibilityReportError", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      console.log("useFlagPhoto", data);
      showToast(data.message, "success");
      setAttachedImages([]);
      setNoteText("");
      onClose();
    }
  }, [data, isDark]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (noteText.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    if (attachedImages.length === 0) {
      setErrorMessage("Please add at least one image");
      return;
    }
    setErrorMessage(null);

    console.log("submit", noteText, attachedImages);

    AnalyticsService.trackGAEventHandler("flagVenueNoteCreate");
    AnalyticsService.trackMx("flagVenueNoteCreate", {
      VenueId: venueDetails.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.types,
      ReviewerName: user?.name,
    });

    mutate({
      noteId: note.business_note_text_id.toString(),
      text: noteText,
      photos: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={true}
      onClose={() => onClose()}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
        closeButton: "hidden",
      }}
    >
      {status === "pending" && <Loader />}
      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-220px)] overflow-y-auto"
        // role="none"
        role="dialog"
        aria-label={i18n.get("flagAccessibility")}
        tabIndex={0}
        ref={firstFocusRef}
      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          >
            {i18n.get("flagVenueNote")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div
                  className="self-stretch !text-primary-500 dark:!text-primary-300 paragraph-xsmall-semibold"
                  tabIndex={0}
                >
                  {venueDetails.name}
                </div>
                <h1
                  className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                  tabIndex={0}
                >
                  {i18n.get("helpUsCatchEveryError")}
                </h1>
              </div>
              <div
                className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold pt-1"
                tabIndex={0}
              >
                {i18n.get("flagVenueNoteMessage")}
              </div>

              <div className="border w-full border-primary-150 dark:border-neutral-400 mt-2 h-[1px]"></div>
            </div>

            <div className="self-stretch md:px-1 rounded-lg justify-start items-center gap-4 inline-flex">
              <div className="w-[24px] h-[24px] relative">
                <img
                  src={isDark ? "/images/Info_dark.svg" : "/images/Info.svg"}
                  alt=""
                  aria-hidden="true"
                  className="w-6 h-6 self-start cursor-pointer"
                />
              </div>
              <div className="h-14 flex-col justify-center items-start gap-1 inline-flex">
                <div
                  className="w-full mobile-h2-medium-semi-bold text-primary-900 dark:text-primary-100"
                  tabIndex={0}
                >
                 {i18n.get("venueNote")}
                </div>
                <div className="paragraph-small-regular" tabIndex={0}>
                  {note.action_title}
                </div>
              </div>
            </div>

            <div className="border w-full border-primary-150 dark:border-neutral-400 mb-2 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  {i18n.get("tellUsWhatIsIncorrect")}
                </label>
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhatIsIncorrect")}
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={noteText}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNoteText(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="button-small-medium text-neutral-500 mt-3"
                  tabIndex={0}
                >
                  <span className={noteText.length < 5 ? "text-red-600" : ""}>
                    {noteText.length}
                  </span>
                  /150 Characters - at least 5 characters required
                </div>

                {errorMessage && (
                  <div className="text-red-600" role="alert" tabIndex={0}>
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto" tabIndex={0}>
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <button
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index
                            )
                          );
                        }}
                        className="absolute top-2 right-3 m-2"
                        aria-label={`Remove attached image ${index + 1}`}
                      >
                        <div className="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex">
                          <div className="w-4 h-4 relative flex-col justify-start items-start flex">
                            <img
                              src="/images/Close.svg"
                              alt="Remove image"
                              className="w-4 h-4"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900 z-1000">
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-[327px] justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-primary-50 dark:bg-dark-800 rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
                tabIndex={0}
              >
                <div className="text-center text-primary-800 dark:text-primary-150 mobile-h3-small-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={noteText.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit flag"
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>

        <button
          onClick={() => {
            setAttachedImages([]);
            setNoteText("");
            onClose();
          }}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </Modal>
  );
}

export default FlagVenueNote;
